import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';

import SectionLoader, {
  LOADER_TYPE,
} from '@/src/components/common/Elements/Spinner/SectionLoader/SectionLoader';
import { mapEmailEntryListItem } from '@/src/components/project/Inbox2/models/EmailEntry';
import {
  EmailEntryFiles,
  EmailEntryToggleableListItem,
  View,
  SidebarEmailThreadEntry,
  EmailEntryType,
} from '@/src/components/project/Inbox2/types';
import useInboxSearchParams from '@/src/components/project/Inbox2/useInboxSearchParams';
import { Authorization } from '@/src/lib/authorization/authorization';

import EmailEntry from './EmailEntry/EmailEntry';
import EmailThreadCounter from './EmailThreadCounter/EmailThreadCounter';
import FileEntryViewer from './FileEntryViewer/FileEntryViewer';

import { useEmailEntries } from '../../../api/getEmailEntries';
import { useEmailThreadEntryViewer } from '../../../store/emailThreadEntryViewer.store';
import CreateReply from '../CreateReply/CreateReply';
import { useInbox } from '@/src/components/project/Inbox2/store/inbox.store';

const COLLAPSED_COUNT_LIMIT = 5;
export const EMAIL_THREAD_PAGE_LIMIT = 25;

const mapEmailEntryToggleableListItem = ({
  rawResponse,
  inboxEntryId,
  emailEntriesSettings,
  emailEntriesSettingsKeys,
}): EmailEntryToggleableListItem => {
  return {
    emailEntryListItem: mapEmailEntryListItem({ ...rawResponse, inboxEntryId }),
    isExpanded: rawResponse.isExpanded,
    isSelected: rawResponse.isSelected,
    isLatest: rawResponse.isLatest,
    isVisible:
      rawResponse.isLatest === true
        ? false
        : emailEntriesSettings[rawResponse.id]
        ? emailEntriesSettings[rawResponse.id].isVisible
        : emailEntriesSettingsKeys.length >= EMAIL_THREAD_PAGE_LIMIT,
  };
};

const getRemainingCount = ({ totalCount, oldEmailEntries, latestEmailEntries }) => {
  if (!totalCount) {
    return null;
  }

  if (totalCount < COLLAPSED_COUNT_LIMIT) {
    return 0;
  }

  if (oldEmailEntries.length > EMAIL_THREAD_PAGE_LIMIT && latestEmailEntries.length > 1) {
    return totalCount - (oldEmailEntries.length + latestEmailEntries.length);
  }

  return totalCount - (oldEmailEntries.length + latestEmailEntries.length);
};

type EmailEntriesSettings = Record<string, { isVisible: boolean }>;

type EmailEntriesProps = {
  emailThreadEntry: SidebarEmailThreadEntry;
};

const EmailEntries = ({ emailThreadEntry }: EmailEntriesProps) => {
  const { projectId } = useParams<{ projectId: string }>();
  const {
    replyType,
    emailEntryToReply,
    emailFileEntry,
    selectedEmailEntry,
    setEmailFileEntry,
    setSelectedEmailEntryListItem,
  } = useEmailThreadEntryViewer();
  const { view } = useInboxSearchParams();
  const emailEntriesQuery = useEmailEntries({
    projectId,
    inboxEntryId: emailThreadEntry.id,
    limit: EMAIL_THREAD_PAGE_LIMIT,
    trash: view === View.Trash,
  });
  const [emailEntries, setEmailEntries] = useState<EmailEntryToggleableListItem[]>([]);
  const [oldEmailEntries, setOldEmailEntries] = useState<EmailEntryToggleableListItem[]>([]);
  const [emailEntriesSettings, setEmailEntriesSettings] = useState<EmailEntriesSettings>({});
  const [latestEmailEntries, setLatestEmailEntries] = useState<EmailEntryToggleableListItem[]>([]);
  const isFirstFetch = useRef(true);
  const totalCount = emailEntriesQuery.data?.pages[0].data['total-count'] || 0;
  const remainingCount = getRemainingCount({
    totalCount,
    oldEmailEntries,
    latestEmailEntries,
  });

  useEffect(() => {
    if (!emailEntriesQuery.data) {
      return;
    }

    const emailEntriesSettingsKeys = Object.keys(emailEntriesSettings);

    const resEmailEntries: EmailEntryToggleableListItem[] = emailEntriesQuery.data.pages.flatMap(
      (page) => {
        const emails = page.data.emails.map(
          (r): EmailEntryToggleableListItem =>
            mapEmailEntryToggleableListItem({
              rawResponse: r,
              inboxEntryId: emailThreadEntry.id,
              emailEntriesSettings,
              emailEntriesSettingsKeys,
            })
        );

        return emails.reverse();
      }
    );

    // Will only run at 1st fetch
    if (isFirstFetch.current) {
      const newEmailEntriesSettings = {};

      resEmailEntries.forEach((ee) => {
        newEmailEntriesSettings[ee.emailEntryListItem.id] = {
          isVisible: ee.isVisible,
        };
      });

      setEmailEntriesSettings(newEmailEntriesSettings);

      if (resEmailEntries.length > 0) {
        setSelectedEmailEntryListItem(
          resEmailEntries[resEmailEntries.length - 1].emailEntryListItem
        );
      }
    }

    // Base copy of all
    setEmailEntries(resEmailEntries);

    const isDefaultSelected = resEmailEntries.find((e) => e.isSelected);

    if (isDefaultSelected) {
      setSelectedEmailEntryListItem(resEmailEntries[resEmailEntries.length - 1].emailEntryListItem);
    }

    if (resEmailEntries.length <= COLLAPSED_COUNT_LIMIT) {
      setLatestEmailEntries([...resEmailEntries]);
      setOldEmailEntries([]);
    } else {
      const initialLatestEmailEntryListItem =
        resEmailEntries.length < EMAIL_THREAD_PAGE_LIMIT
          ? resEmailEntries[resEmailEntries.length - 1] // From current logic, last item of page 1 is the latest
          : resEmailEntries[EMAIL_THREAD_PAGE_LIMIT - 1];

      // We collapse and split list (old list vs latest list)
      const resOldEmailEntries = resEmailEntries.filter(
        (ee) =>
          ee.isVisible &&
          ee.emailEntryListItem.id !== initialLatestEmailEntryListItem.emailEntryListItem.id
      );

      setOldEmailEntries(
        resOldEmailEntries.length > 0 ? resOldEmailEntries : resEmailEntries.slice(0, 2)
      );
      const resLatestEmailEntries = resEmailEntries.filter((e) => e.isLatest);

      if (resLatestEmailEntries.length > 0) {
        setLatestEmailEntries([...resLatestEmailEntries]);
      } else {
        setLatestEmailEntries([initialLatestEmailEntryListItem]);
      }
    }

    isFirstFetch.current = false;
  }, [
    emailThreadEntry.id,
    emailEntriesQuery.data,
    emailEntriesSettings,
    setSelectedEmailEntryListItem,
  ]);

  const handleLoadMore = useCallback(() => {
    // Some items already loaded and only hidden in list
    if (
      oldEmailEntries.length < emailEntries.length &&
      oldEmailEntries.length < EMAIL_THREAD_PAGE_LIMIT - 1 // Offset 1 is from latest
    ) {
      const lastVisibleIndex = oldEmailEntries.length;
      const newEmailEntriesSettings = {};

      emailEntries.forEach((ee, index) => {
        if (index < lastVisibleIndex + EMAIL_THREAD_PAGE_LIMIT) {
          newEmailEntriesSettings[ee.emailEntryListItem.id] = {
            isVisible: ee.isLatest ? false : true,
          };
        }
      });

      setEmailEntriesSettings(newEmailEntriesSettings);
    } else {
      emailEntriesQuery.fetchNextPage();
    }
  }, [oldEmailEntries.length, emailEntries, emailEntriesQuery]);

  useEffect(() => {
    return () => {
      setEmailFileEntry(null);
    };
  }, []);

  const { isMarkReadClicked, isMarkAsMiscellaneousClicked } = useInbox();

  const showLoading =
    emailEntriesQuery.isFetchingNextPage ||
    emailEntriesQuery.isLoading ||
    isMarkReadClicked ||
    isMarkAsMiscellaneousClicked;

  return (
    <>
      {showLoading && <SectionLoader loaderType={LOADER_TYPE.screen} />}
      {emailFileEntry && !showLoading && (
        <React.Fragment>
          <FileEntryViewer
            emailId={selectedEmailEntry.id}
            inboxEntryId={selectedEmailEntry.inboxEntryId}
            emailFileEntry={emailFileEntry}
            files={selectedEmailEntry.files as EmailEntryFiles}
            onSetEmailFileEntry={setEmailFileEntry}
            isSentEmailType={selectedEmailEntry.emailType === EmailEntryType.SENT}
          />
        </React.Fragment>
      )}

      <div>
        {oldEmailEntries.map((emailEntryItem) => {
          return (
            <EmailEntry
              key={`ees_${emailEntryItem.emailEntryListItem.id}`}
              emailEntryToggleableListItem={emailEntryItem}
            />
          );
        })}

        {/* Condition for fallback of currently incorrect pagination "oldEmailEntries.length > 0" */}
        {remainingCount > 0 && oldEmailEntries.length > 0 && (
          <EmailThreadCounter
            key={'emailEntry__loadMore'}
            remainingCount={remainingCount}
            onLoadMore={handleLoadMore}
          />
        )}

        {latestEmailEntries.map((emailEntryItem, index) => {
          const isLastItem = index === latestEmailEntries.length - 1;

          return (
            <EmailEntry
              key={`ees_${emailEntryItem.emailEntryListItem.id}`}
              emailEntryToggleableListItem={emailEntryItem}
              isLastItem={isLastItem}
            />
          );
        })}

        {replyType && emailEntryToReply && (
          <React.Fragment>
            <Authorization policyCheck={emailEntryToReply.permissions.createReply}>
              <CreateReply
                key={`cr_${emailEntryToReply.id}`}
                projectId={projectId}
                emailEntry={emailEntryToReply}
              />
            </Authorization>
          </React.Fragment>
        )}
      </div>
    </>
  );
};

export default EmailEntries;
